import type { Product } from 'payments-api/model';
import clsx from 'clsx';
import { capitalize } from 'lodash-es';
import React from 'react';

import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';

import type { ItemTableItem } from './ItemTable';
import { FormatBillingAmount } from './formatBillingAmount';
import { ItemColorSquare } from './ItemColorSquare';
import { PaymentsModal } from './PaymentsModal';
import { RoleColorDot } from './RoleColorDot';

const LabelRow = ({
  label,
  children,
  last,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) => (
  <div
    className={clsx(
      'flex items-center',
      !last && 'border-b-[0.5px] border-main-200'
    )}
  >
    <div
      className={clsx(
        'px-f1 py-f1 text-main-500 border-r-[0.5px] border-main-200 shrink-0',
        kLabelColumnWidth
      )}
    >
      {label}
    </div>
    <div className="px-f1 py-f1 text-main flex-1">{children}</div>
  </div>
);

type Props = {
  product: Product;
  /** Optional line-item context when opened from an invoice */
  lineItem?: ItemTableItem;
  onClose: () => void;
};

export const ProductDetailModal = ({
  product,
  lineItem,
  onClose,
}: Props): JSX.Element => {
  const paidByRoles = product.paid_by_roles.map((r) => r.name);
  const paidToRoles = product.paid_to_roles.map((r) => r.name);

  const lineItemPrecision = lineItem
    ? currencyPrecisionMap[lineItem.currencyCode] || 2
    : 2;
  const lineItemQuantity = lineItem?.quantity ?? 1;
  const lineItemUnitPrice = lineItem
    ? Number(lineItem.unitPrice ?? lineItem.amount)
    : 0;

  return (
    <PaymentsModal
      data-testid="product-detail-modal"
      onClose={onClose}
      width="sm"
      body={
        <div className="flex flex-col w-full text-xmd font-helvetica">
          {/* Content with border */}
          <div className="border-[0.5px] border-main-200">
            {/* Header — 3 rows high */}
            <div className="flex items-start p-f1 h-f12 border-b-[0.5px] border-main-200">
              <span className="flex items-center gap-x-f1">
                <ItemColorSquare productId={product.code} size={20} />
                <span className="text-lg text-main">{product.code}</span>
              </span>
            </div>
            {/* Product fields */}
            {product.description && (
              <LabelRow label="Description">{product.description}</LabelRow>
            )}

            {paidByRoles.length > 0 && (
              <LabelRow label="Paid By">
                {paidByRoles.map((r) => capitalize(r)).join(', ')}
              </LabelRow>
            )}

            {paidToRoles.length > 0 && (
              <LabelRow label="Paid To">
                <span className="flex flex-wrap items-center gap-x-f1 gap-y-[4px]">
                  {paidToRoles.map((r) => (
                    <span key={r} className="flex items-center gap-x-[6px]">
                      <RoleColorDot role={r} />
                      <span>{capitalize(r)}</span>
                    </span>
                  ))}
                </span>
              </LabelRow>
            )}

            <LabelRow label="Created" last={!lineItem}>
              {new Date(product.created).toLocaleDateString()}
            </LabelRow>

            {/* Line-item context (when opened from an invoice) */}
            {lineItem && (
              <>
                <div className="flex items-center border-b-[0.5px] border-main-200 border-t-[0.5px] mt-[-0.5px]">
                  <div className="px-f1 py-f1 text-main-500 flex-1">
                    Line Item
                  </div>
                </div>
                <LabelRow label="Type">{lineItem.type}</LabelRow>
                <LabelRow label="Qty">{lineItemQuantity}</LabelRow>
                <LabelRow label="Price">
                  <FormatBillingAmount
                    amount={lineItemUnitPrice}
                    currencyCode={lineItem.currencyCode}
                    currencyPrecision={lineItemPrecision}
                  />
                </LabelRow>
                <LabelRow label="Due" last>
                  <FormatBillingAmount
                    amount={Number(lineItem.amount)}
                    currencyCode={lineItem.currencyCode}
                    currencyPrecision={lineItemPrecision}
                  />
                </LabelRow>
              </>
            )}
          </div>

          {/* Edit button */}
          <div className="pt-f2">
            <button
              type="button"
              disabled
              className={clsx(
                'flex items-center justify-center',
                'p-[4px] px-[9px]',
                'font-helvetica text-xmd rounded-[2px]',
                'bg-main-200 text-main-500 cursor-not-allowed',
                'transition-colors'
              )}
            >
              Edit
            </button>
          </div>
        </div>
      }
    />
  );
};
